import React, { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
} from "react-router-dom";
import usePageTracking from "./hooks/usePageTracking";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider } from "./contexts/AuthContext";
import Navbar from "./components/Navbar";
import Home from "./components/Home";
import Services from "./components/Services";
import About from "./components/About";
import CitiTekConsultation from "./components/CitiTekConsultation";
import OurWork from "./components/OurWork";
import Footer from "./components/Footer";
import NotFound from "./components/NotFound";
import LoginPage from "./pages/LoginPage";
import OurWorkPage from "./portfolio/OurWorkPage";
import ServiceLandingPage from "./pages/ServiceLandingPage";
import LocationLandingPage from "./pages/LocationLandingPage";
import LoadingSpinner from "./components/LoadingSpinner";
import ProtectedRoute from "./components/ProtectedRoute";

// Lazy-load admin dashboard (code splitting)
const AdminDashboard = lazy(
  () => import("./components/dashboard/AdminDashboard"),
);
const DashboardOverview = lazy(
  () => import("./components/dashboard/DashboardOverview"),
);
const ConsultationsDashboard = lazy(
  () => import("./components/dashboard/ConsultationsDashboard"),
);
const ContactsDashboard = lazy(
  () => import("./components/dashboard/ContactsDashboard"),
);
const RegisterUser = lazy(() => import("./components/dashboard/RegisterUser"));
const UserManagement = lazy(
  () => import("./components/dashboard/UserManagement"),
);
const SecurityMonitoring = lazy(
  () => import("./components/dashboard/SecurityMonitoring"),
);
const AnalyticsDashboard = lazy(
  () => import("./components/dashboard/AnalyticsDashboard"),
);

// Activates page-view tracking for all non-admin public routes
const TrackingWrapper = () => {
  usePageTracking();
  return <Outlet />;
};

// Layout component for public pages with Navbar/Footer
const PublicLayout = () => (
  <div className="App min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
    <Navbar />
    <Outlet />
    <Footer />
  </div>
);

// Main page combines all scroll sections
const MainPage = () => (
  <>
    <Home />
    <Services />
    <OurWork />
    <About />
    <CitiTekConsultation />
  </>
);

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <Router>
            <Routes>
              {/* Admin Dashboard Routes - No Navbar/Footer */}
              <Route
                path="/admin/dashboard"
                element={
                  <ProtectedRoute requireAdmin={true}>
                    <Suspense fallback={<LoadingSpinner />}>
                      <AdminDashboard />
                    </Suspense>
                  </ProtectedRoute>
                }
              >
              <Route
                index
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <DashboardOverview />
                  </Suspense>
                }
              />
              <Route
                path="consultations"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <ConsultationsDashboard />
                  </Suspense>
                }
              />
              <Route
                path="contacts"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <ContactsDashboard />
                  </Suspense>
                }
              />
              <Route
                path="users"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <UserManagement />
                  </Suspense>
                }
              />
              <Route
                path="register-user"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <RegisterUser />
                  </Suspense>
                }
              />
              <Route
                path="security"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <SecurityMonitoring />
                  </Suspense>
                }
              />
              <Route
                path="analytics"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <AnalyticsDashboard />
                  </Suspense>
                }
              />
            </Route>

            {/* Public Routes - With Navbar/Footer + page tracking */}
            <Route element={<PublicLayout />}>
              <Route element={<TrackingWrapper />}>
                <Route path="/" element={<MainPage />} />
                <Route path="/our-work" element={<OurWorkPage />} />
                <Route path="/services/:slug" element={<ServiceLandingPage />} />
                <Route path="/locations/:slug" element={<LocationLandingPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Route>
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
